
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsersRemainingLeaves } from "../../redux/actions/leaveActions";
import {
  FiSearch,
  FiDownload,
  FiChevronLeft,
  FiChevronRight,
  FiUser,
  FiPhone,
  FiMail,
  FiBriefcase,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
  FiAlertCircle,
  FiFileText
} from "react-icons/fi";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const EmployeesRemainingLeaves = () => {
  const dispatch = useDispatch();

  const {
    usersRemainingLeaves = [],
    pagination,
    loading,
    error,
  } = useSelector((state) => state.leave);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exportFormat, setExportFormat] = useState("csv"); // "csv" or "excel"
  
  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchUsersRemainingLeaves(currentPage, itemsPerPage));
  }, [dispatch, currentPage]);

  // 🔹 Search filter
  const filteredUsers = useMemo(() => {
    return usersRemainingLeaves.filter((user) => {
      const term = searchTerm.toLowerCase();
      return (
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.phone?.toLowerCase().includes(term) ||
        user.designation?.toLowerCase().includes(term)
      );
    });
  }, [usersRemainingLeaves, searchTerm]);

  // 🔹 Collect all leave types dynamically
  const leaveTypes = useMemo(() => {
    const types = Array.from(
      new Set(
        usersRemainingLeaves.flatMap((u) =>
          u.leaves?.map((l) => l.leave_type_name)
        )
      )
    ).filter(Boolean);
    
    return types.sort();
  }, [usersRemainingLeaves]);

  // Helper function to format data for Excel-style export
  const prepareExcelData = () => {
    // Create header row
    const headers = ["Employee", "Email", "Phone", "Designation"];
    
    // Add leave type headers in your requested format
    leaveTypes.forEach((leaveType) => {
      headers.push(`${leaveType} (Elg)`, `${leaveType} (Avl)`, `${leaveType} (Rmn)`);
    });

    // Create data rows
    const data = usersRemainingLeaves.map((user) => {
      const row = [
        user.name || "",
        user.email || "",
        user.phone || "",
        user.designation || "",
      ];

      // Add leave data for each type
      leaveTypes.forEach((leaveType) => {
        const leaveData = user.leaves?.find((l) => l.leave_type_name === leaveType);
        row.push(
          leaveData?.total_leaves || 0,
          leaveData?.used_leaves || 0,
          leaveData?.remaining_leaves || 0
        );
      });

      return row;
    });

    return { headers, data };
  };

  // Excel Export (XLSX format)
  const handleDownloadExcel = () => {
    const { headers, data } = prepareExcelData();
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create worksheet data
    const wsData = [headers, ...data];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    const colWidths = headers.map(() => ({ wch: 15 }));
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Leave Balances");
    
    // Generate and download file
    const fileName = `Leave_Balances_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // CSV Export (alternative format)
  const handleDownloadCSV = () => {
    const { headers, data } = prepareExcelData();
    
    // Create CSV data with headers
    const csvData = [headers, ...data];
    const csv = Papa.unparse(csvData);
    
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Leave_Balances_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Combined export handler
  const handleExport = () => {
    if (exportFormat === "excel") {
      handleDownloadExcel();
    } else {
      handleDownloadCSV();
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const getLeaveStatusColor = (remaining, total) => {
    const percentage = total > 0 ? (remaining / total) * 100 : 0;
    if (percentage >= 50) return "text-green-600 dark:text-green-400";
    if (percentage >= 25) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const formatNumber = (num) => {
    return num || num === 0 ? num : "-";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Leave Balances...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="text-5xl text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 text-lg mb-2">Error loading data</p>
          <p className="text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Employee Leave Balances
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track remaining leaves across all employees
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-4 lg:mt-0">
          {/* Export Format Selector */}
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setExportFormat("csv")}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${exportFormat === "csv" ? "bg-white dark:bg-gray-700 shadow" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}
            >
              CSV
            </button>
            <button
              onClick={() => setExportFormat("excel")}
              className={`px-3 py-1 rounded-md text-sm transition-colors ${exportFormat === "excel" ? "bg-white dark:bg-gray-700 shadow" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}
            >
              Excel
            </button>
          </div>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl transition-colors"
          >
            {exportFormat === "excel" ? <FiFileText className="text-lg" /> : <FiDownload className="text-lg" />}
            Export {exportFormat.toUpperCase()}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or designation..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Clear Filters */}
          {searchTerm && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 px-4 py-3 rounded-xl transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Info Bar */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <FiCheckCircle className="text-green-500" />
              <span>Green: ≥50% leaves remaining</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <FiAlertCircle className="text-yellow-500" />
              <span>Yellow: 25-50% leaves remaining</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <FiAlertCircle className="text-red-500" />
              <span>Red: &lt;25% leaves remaining</span>
            </div>
          </div>
          
          {/* Excel Format Info */}
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-start gap-2">
              <FiFileText className="text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                  Excel Export Format:
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Columns for each leave type: <strong>Elg</strong> (Eligible/Total), <strong>Avl</strong> (Available/Used), <strong>Rmn</strong> (Remaining)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600 dark:text-gray-400">
          Showing {Math.min(itemsPerPage, filteredUsers.length)} of {usersRemainingLeaves.length} employee records
          {searchTerm && ` (${filteredUsers.length} filtered)`}
        </p>
        {pagination?.total > itemsPerPage && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>Page {currentPage} of {Math.ceil((pagination?.total || 0) / itemsPerPage)}</span>
          </div>
        )}
      </div>

      {/* Table Container */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                {/* Employee Info Header */}
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" rowSpan="2">
                  Employee
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" rowSpan="2">
                  <div className="flex items-center gap-2">
                    <FiBriefcase />
                    Designation
                  </div>
                </th>
                
                {/* Leave Type Headers */}
                {leaveTypes.map((leaveType) => (
                  <th key={leaveType} className="py-4 px-6 text-center text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" colSpan="3">
                    <div className="flex items-center justify-center gap-2">
                      <FiCalendar />
                      {leaveType}
                    </div>
                  </th>
                ))}
              </tr>
              <tr>
                {leaveTypes.map((leaveType) => (
                  <React.Fragment key={`sub-${leaveType}`}>
                    <th className="py-3 px-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Elg
                    </th>
                    <th className="py-3 px-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Avl
                    </th>
                    <th className="py-3 px-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Rmn
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={3 + (leaveTypes.length * 3)} className="py-12 px-6 text-center">
                    <FiUser className="text-4xl text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No employee records found</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm">
                      {searchTerm ? "Try adjusting your search" : "No data available"}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    {/* Employee Info */}
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                          {user.name}
                        </p>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                            <FiMail className="text-xs" />
                            {user.email}
                          </p>
                          {user.phone && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                              <FiPhone className="text-xs" />
                              {user.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Designation */}
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {user.designation || "-"}
                      </span>
                    </td>

                    {/* Leave Data */}
                    {leaveTypes.map((leaveType) => {
                      const leaveData = user.leaves?.find(
                        (l) => l.leave_type_name === leaveType
                      );
                      const total = leaveData?.total_leaves || 0;
                      const used = leaveData?.used_leaves || 0;
                      const remaining = leaveData?.remaining_leaves || 0;
                      
                      return (
                        <React.Fragment key={`${user.id}-${leaveType}`}>
                          <td className="py-4 px-2 text-center text-gray-700 dark:text-gray-300 font-medium">
                            {formatNumber(total)}
                          </td>
                          <td className="py-4 px-2 text-center text-gray-600 dark:text-gray-400">
                            {formatNumber(used)}
                          </td>
                          <td className="py-4 px-2 text-center">
                            <div className={`font-bold ${getLeaveStatusColor(remaining, total)}`}>
                              {formatNumber(remaining)}
                              {remaining < used && remaining > 0 && (
                                <FiTrendingDown className="inline ml-1 text-sm" />
                              )}
                              {remaining > used && remaining > 0 && (
                                <FiTrendingUp className="inline ml-1 text-sm" />
                              )}
                            </div>
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination?.total > itemsPerPage && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft />
            Previous
          </button>

          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Page {currentPage} of {Math.ceil((pagination?.total || 0) / itemsPerPage)}
            </span>
          </div>

          <button
            onClick={() => setCurrentPage(p => 
              Math.min(Math.ceil((pagination?.total || 0) / itemsPerPage), p + 1)
            )}
            disabled={currentPage >= Math.ceil((pagination?.total || 0) / itemsPerPage)}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default EmployeesRemainingLeaves;